import { useMemo, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  format,
  startOfDay,
  endOfDay,
  startOfWeek,
  addDays,
  isWithinInterval,
  parseISO,
} from "date-fns";
import { sv } from "date-fns/locale";
import { usePlacesSearch, type PlaceSearchFilters } from "@/hooks/usePlacesSearch";
import { useExploreEvents } from "@/hooks/useExploreEvents";
import { useExploreProducers } from "@/hooks/useExploreProducers";
import { usePlaceSignals } from "@/hooks/usePlaceSignals";
import { useBatchCovers } from "@/hooks/useBatchCovers";
import { distanceKm } from "@/lib/map/distance";
import type { ExploreResultItem } from "@/types/exploreResult";
import type { ExploreMode } from "@/components/explore/ExploreModeToggle";
import type { UserLocation } from "@/lib/map/geolocation";
import type { RadiusKm } from "@/lib/map/config";
import { EVENT_TYPE_LABELS } from "@/types/events";
import type { ExploreParams } from "@/lib/exploreParams";
import { track } from "@/lib/analytics";
import { supabase } from "@/integrations/supabase/client";
import { getNextUpcomingOccurrence } from "@/lib/fleaMarkets/occurrences";

// EXPLORE_DEBUG: enable verbose debug logging in dev or when VITE_EXPLORE_DEBUG=true.
// Additionally support a URL override `?explore_debug=1` so we can enable logs
// in production for short-lived verification without changing env vars.
let EXPLORE_DEBUG = import.meta.env.VITE_EXPLORE_DEBUG === "true" || import.meta.env.DEV;
try {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    if (params.get('explore_debug') === '1') EXPLORE_DEBUG = true;
  }
} catch (err) {
  /* ignore in non-browser environments */
}

function dbg(label: string, ...args: unknown[]) {
  if (EXPLORE_DEBUG) console.log(`[Explore][${label}]`, ...args);
}

// ────────────────────────────────────────────────────────────
// Bucketing helpers (low-cardinality props for PostHog)
// ────────────────────────────────────────────────────────────
function bucketCount(n: number): string {
  if (n <= 0) return "0";
  if (n <= 1) return "1";
  if (n <= 3) return "2–3";
  if (n <= 5) return "4–5";
  if (n <= 10) return "6–10";
  if (n <= 25) return "11–25";
  if (n <= 50) return "26–50";
  if (n <= 100) return "51–100";
  return "101+";
}

function bucketRadiusKm(r: number): string {
  if (r <= 1) return "≤1km";
  if (r <= 3) return "1–3km";
  if (r <= 5) return "3–5km";
  if (r <= 10) return "5–10km";
  if (r <= 25) return "10–25km";
  if (r <= 50) return "25–50km";
  return "50km+";
}

function bucketSort(sort: string): string {
  if (sort === "nearest") return "nearest";
  if (sort === "az") return "az";
  if (sort === "soonest") return "soonest";
  if (sort === "standard") return "standard";
  return "default";
}

function bucketBool(b: boolean): "yes" | "no" {
  return b ? "yes" : "no";
}

// ── Stable key helper ──
export function itemKey(item: ExploreResultItem): string {
  return `${item.kind}:${item.id}`;
}

interface UseExploreResultsOptions {
  mode: ExploreMode;
  placeFilters: PlaceSearchFilters;
  nearMeActive: boolean;
  userLocation: UserLocation | null;
  radiusKm: RadiusKm;
  favActive: boolean;
  favIds: Set<string>;
  searchQuery?: string;
  /** URL-driven filter/sort params */
  exploreParams?: ExploreParams;
}

interface ExploreFleaMarketDiscoveryItem {
  id: string;
  title: string;
  description: string | null;
  placeName: string;
  areaName: string | null;
  nextStartAt: string;
  nextEndAt: string | null;
  lat: number | null;
  lng: number | null;
}

async function fetchExploreFleaMarkets(): Promise<ExploreFleaMarketDiscoveryItem[]> {
  const { data: marketsData, error: marketsError } = await supabase
    .from('producer_flea_markets' as any)
    .select('id, title, description, place_id')
    .eq('status', 'public');

  if (marketsError) throw marketsError;

  const markets = (marketsData ?? []) as unknown as Array<{
    id: string;
    title: string;
    description: string | null;
    place_id: string;
  }>;

  if (markets.length === 0) return [];

  const marketIds = markets.map((market) => market.id);
  const placeIds = Array.from(new Set(markets.map((market) => market.place_id)));

  const [{ data: occurrencesData, error: occurrencesError }, { data: placesData, error: placesError }] = await Promise.all([
    supabase
      .from('producer_flea_market_occurrences' as any)
      .select('flea_market_id, start_at, end_at, status')
      .in('flea_market_id', marketIds),
    supabase
      .from('places' as any)
      .select('id, name, landskap, municipality, lat, lng')
      .in('id', placeIds),
  ]);

  if (occurrencesError) throw occurrencesError;
  if (placesError) throw placesError;

  const placeById = new Map(
    ((placesData ?? []) as unknown as Array<{
      id: string;
      name: string;
      landskap: string | null;
      municipality: string | null;
      lat: number | null;
      lng: number | null;
    }>).map((place) => [place.id, place]),
  );

  const occurrencesByMarketId = new Map<string, Array<{ start_at: string; end_at: string | null; status?: string }>>();
  ((occurrencesData ?? []) as unknown as Array<{ flea_market_id: string; start_at: string; end_at: string | null; status?: string }>).forEach((row) => {
    const list = occurrencesByMarketId.get(row.flea_market_id) ?? [];
    list.push({
      start_at: row.start_at,
      end_at: row.end_at,
      status: row.status,
    });
    occurrencesByMarketId.set(row.flea_market_id, list);
  });

  return markets
    .map((market) => {
      const place = placeById.get(market.place_id);
      if (!place) return null;

      const nextOccurrence = getNextUpcomingOccurrence(occurrencesByMarketId.get(market.id) ?? []);
      if (!nextOccurrence) return null;

      return {
        id: market.id,
        title: market.title,
        description: market.description,
        placeName: place.name,
        areaName: place.landskap ?? place.municipality,
        nextStartAt: nextOccurrence.start_at,
        nextEndAt: nextOccurrence.end_at ?? null,
        lat: place.lat,
        lng: place.lng,
      } satisfies ExploreFleaMarketDiscoveryItem;
    })
    .filter((item): item is ExploreFleaMarketDiscoveryItem => item !== null);
}

/**
 * Single source of truth for Explore data.
 * Drives list, map markers, selection, and preview.
 *
 * Restore legacy explore semantics:
 * - Always fetches producers (cheap) as fallback content
 * - If primary mode returns 0 results, falls back to producers
 * - Near Me without geolocation silently falls back to global results
 */
export function useExploreResults(options: UseExploreResultsOptions) {
  const {
    mode,
    placeFilters,
    nearMeActive,
    userLocation,
    radiusKm,
    favActive,
    favIds,
    searchQuery,
    exploreParams,
  } = options;

  // Always fetch producers (lightweight, 1 query) — used as fallback
  const { data: allProducers, isLoading: producersLoading } = useExploreProducers(true);
  const { data: placesResult, isLoading: placesLoading } = usePlacesSearch(placeFilters, mode === "places");
  const { events: allEvents, loading: eventsLoading } = useExploreEvents();
  const { data: allFleaMarkets = [], isLoading: fleaMarketsLoading } = useQuery({
    queryKey: ['explore-flea-markets'],
    queryFn: fetchExploreFleaMarkets,
    enabled: mode === 'do',
    staleTime: 30_000,
  });

  // ── Batch cover images from media_assets ──
  const placeIds = useMemo(() => (placesResult?.items || []).map((p) => p.id), [placesResult]);
  const producerIds = useMemo(() => (allProducers || []).map((p) => p.id), [allProducers]);
  const eventIds = useMemo(() => allEvents.map((e) => e.id), [allEvents]);
  const fleaMarketIds = useMemo(() => allFleaMarkets.map((market) => market.id), [allFleaMarkets]);

  const { coverMap: placeCoverMap } = useBatchCovers("place", mode === "places" ? placeIds : [], 320);
  const { coverMap: producerCoverMap } = useBatchCovers("producer", mode === "farms" ? producerIds : [], 320);
  const { coverMap: eventCoverMap } = useBatchCovers("event", mode === "do" ? eventIds : [], 320);
  const { coverMap: fleaMarketCoverMap } = useBatchCovers("flea_market", mode === "do" ? fleaMarketIds : [], 320);

  // ── Batch place signals (farm shop / farm sale) ──
  const { data: placeSignalsMap } = usePlaceSignals(mode === "places" ? placeIds : []);

  const isLoading =
    mode === "places" ? placesLoading || producersLoading
    : mode === "farms" ? producersLoading
    : eventsLoading || producersLoading || fleaMarketsLoading;

  // Robust coord normalization: null-safe, comma→dot, range check, auto-swap, block Null Island
  function parseCoord(v: unknown): number | null {
    if (v === null || v === undefined) return null;
    const s = String(v).trim();
    if (!s || s === "null" || s === "undefined") return null;
    const n = parseFloat(s.replace(",", "."));
    return Number.isFinite(n) ? n : null;
  }
  function normalizeCoords(
    latCandidate: unknown,
    lngCandidate: unknown
  ): { lat: number | null; lng: number | null } {
    const lat = parseCoord(latCandidate);
    const lng = parseCoord(lngCandidate);
    const isLat = (x: number | null): x is number => x !== null && x >= -90 && x <= 90;
    const isLng = (x: number | null): x is number => x !== null && x >= -180 && x <= 180;

    if (isLat(lat) && isLng(lng)) {
      if (lat === 0 && lng === 0) return { lat: null, lng: null }; // block Null Island
      return { lat, lng };
    }
    if (isLat(lng) && isLng(lat)) {
      if (lng === 0 && lat === 0) return { lat: null, lng: null };
      return { lat: lng, lng: lat };
    }
    return { lat: null, lng: null };
  }
  const safeCoords = (latV: unknown, lngV: unknown) => normalizeCoords(latV, lngV);

  const results = useMemo((): ExploreResultItem[] => {
    let items: ExploreResultItem[] = [];

    // ── Normalize per mode ──
    if (mode === "places") {
      items = (placesResult?.items || []).map((p) => {
        const c = safeCoords(p.lat, p.lng);
        const signals = placeSignalsMap?.get(p.id);
        return {
          id: p.id,
          kind: "place" as const,
          slug: p.slug,
          title: p.name,
          subtitle: p.subtitle,
          shortDescription: p.shortDescription,
          image: placeCoverMap.get(p.id) || p.coverImageUrl,
          lat: c.lat,
          lng: c.lng,
          href: p.href,
          badges: p.tags.slice(0, 2),
          dateLabel: null,
          hasFarmShop: signals?.has_farm_shop ?? false,
          hasFarmSaleAlcohol: signals?.has_farm_sale_alcohol ?? false,
          hasAccommodation: signals?.has_accommodation ?? false,
          primaryAreaName: p.primaryAreaName,
          primaryAreaSlug: p.primaryAreaSlug,
        };
      });
    } else if (mode === "farms") {
      items = (allProducers || []).map((p) => {
        const c = safeCoords(p.lat, p.lng);
        return {
          id: p.id,
          kind: "producer" as const,
          slug: p.slug,
          title: p.name,
          subtitle: p.subtitle,
          shortDescription: p.shortDescription,
          image: producerCoverMap.get(p.id) || p.coverImageUrl,
          lat: c.lat,
          lng: c.lng,
          href: p.href,
          badges: p.categories.slice(0, 2),
          dateLabel: null,
          hasFarmShop: p.hasFarmShop,
          hasFarmSaleAlcohol: p.hasFarmSaleAlcohol,
        };
      });
    } else {
      // "do" mode — activities
      const showEvents = !exploreParams || exploreParams.contentTypes.includes("events");
      const showOutings = !exploreParams || exploreParams.contentTypes.includes("outings");

      const filteredEvents = allEvents.filter((e) => {
        const isOuting = e.eventType === "outing";
        if (isOuting && !showOutings) return false;
        if (!isOuting && !showEvents) return false;

        // Apply "when" filter — open/anytime/season always pass
        if (e.timingMode === 'open' || e.timingMode === 'anytime' || e.timingMode === 'season') return true;

        if (exploreParams && exploreParams.when !== "upcoming") {
          const eventDate = parseISO(e.startDate);
          const today = new Date();
          if (exploreParams.when === "today") {
            if (!isWithinInterval(eventDate, { start: startOfDay(today), end: endOfDay(today) })) return false;
          } else if (exploreParams.when === "weekend") {
            const nextSat = startOfDay(addDays(startOfWeek(today, { weekStartsOn: 1 }), 5));
            const nextSun = endOfDay(addDays(nextSat, 1));
            if (!isWithinInterval(eventDate, { start: nextSat, end: nextSun })) return false;
          }
        }
        return true;
      });

      const mappedEvents = filteredEvents.map((e) => {
        const left = e.maxSpots - e.bookedSpots;
        const isTrip = e.eventType === "outing";
        const c = safeCoords(e.lat, e.lng);

        // Date label based on timingMode
        let dateLabel: string | null = null;
        if (e.timingMode === 'open' || e.timingMode === 'anytime') {
          dateLabel = "Öppet under säsong";
        } else if (e.timingMode === 'season') {
          const seasons = (e.outingTags || [])
            .filter((t: string) => t.startsWith('season:'))
            .map((t: string) => {
              const s = t.replace('season:', '');
              return s.charAt(0).toUpperCase() + s.slice(1);
            });
          dateLabel = seasons.length > 0 ? seasons.join(' · ') : "Bäst under säsong";
        } else {
          dateLabel = format(new Date(e.startDate), "d MMM", { locale: sv });
        }

        return {
          id: e.id,
          kind: "activity" as const,
          slug: e.id,
          title: e.title,
          subtitle: e.producerName + (e.producerCity ? `, ${e.producerCity}` : ""),
          shortDescription: e.shortDescription,
          image: eventCoverMap.get(e.id) || e.coverImageUrl,
          lat: c.lat,
          lng: c.lng,
          href: e.href,
          badges: [EVENT_TYPE_LABELS[e.eventType] || e.eventType],
          dateLabel,
          timingMode: e.timingMode,
          outingTags: e.outingTags || [],
          startDate: e.startDate,
          endDate: e.endDate,
          isTrip,
          priceText: null, // Never show price on Explore event cards
          spotsInfo: isTrip
            ? null
            : left <= 0
            ? "Fullbokat"
            : left <= 5
            ? `${left} platser kvar`
            : null,
          address: e.address,
          includesFarmSale: e.includesFarmSale,
          includesFarmSaleAlcohol: e.includesFarmSaleAlcohol,
          _startDate: e.startDate,
          _timingMode: e.timingMode,
        };
      });

      const filteredFleaMarkets = allFleaMarkets.filter((market) => {
        if (!exploreParams || exploreParams.when === 'upcoming') return true;
        const start = parseISO(market.nextStartAt);
        if (exploreParams.when === 'today') {
          const today = new Date();
          return isWithinInterval(start, { start: startOfDay(today), end: endOfDay(today) });
        }
        if (exploreParams.when === 'weekend') {
          const today = new Date();
          const nextSat = startOfDay(addDays(startOfWeek(today, { weekStartsOn: 1 }), 5));
          const nextSun = endOfDay(addDays(nextSat, 1));
          return isWithinInterval(start, { start: nextSat, end: nextSun });
        }
        return true;
      });

      const mappedFleaMarkets = filteredFleaMarkets.map((market) => {
        const c = safeCoords(market.lat, market.lng);
        const subtitle = market.areaName
          ? `${market.placeName} · ${market.areaName}`
          : market.placeName;

        return {
          id: market.id,
          kind: 'activity' as const,
          slug: market.id,
          title: market.title,
          subtitle,
          shortDescription: market.description,
          image: fleaMarketCoverMap.get(market.id) || null,
          lat: c.lat,
          lng: c.lng,
          href: `/loppis/${market.id}`,
          badges: ['Loppis'],
          dateLabel: null,
          timingMode: 'date_time' as const,
          startDate: market.nextStartAt,
          endDate: market.nextEndAt ?? undefined,
          isTrip: true,
          priceText: null,
          spotsInfo: null,
          _startDate: market.nextStartAt,
          _timingMode: 'date_time',
        };
      });

      items = [...mappedEvents, ...mappedFleaMarkets];

      // Sort: date_time first by start, then anytime/season at end
      const sortOpt = exploreParams?.sort ?? "soonest";
      if (sortOpt === "soonest") {
        items.sort((a, b) => {
          const aMode = (a as any)._timingMode || 'date_time';
          const bMode = (b as any)._timingMode || 'date_time';
          const aIsDateTime = aMode === 'date_time';
          const bIsDateTime = bMode === 'date_time';
          if (aIsDateTime && !bIsDateTime) return -1;
          if (!aIsDateTime && bIsDateTime) return 1;
          if (aIsDateTime && bIsDateTime) {
            const aDate = new Date((a as any)._startDate || "").getTime() || 0;
            const bDate = new Date((b as any)._startDate || "").getTime() || 0;
            return aDate - bDate;
          }
          return a.title.localeCompare(b.title, "sv");
        });
      } else if (sortOpt === "az") {
        items.sort((a, b) => a.title.localeCompare(b.title, "sv"));
      }
      // "nearest" handled later in distance block
    }

    // ── Forgiving fallback: if primary mode is empty AND done loading, show producers ──
    // "do" mode is activity-only — never fall back to producers (canonical rule)
    const primaryStillLoading = mode === "places" ? placesLoading : mode === "do" ? eventsLoading : false;
    if (items.length === 0 && mode === "places" && !primaryStillLoading && (allProducers?.length ?? 0) > 0) {
      items = (allProducers || []).map((p) => {
        const c = safeCoords(p.lat, p.lng);
        return {
          id: p.id,
          kind: "producer" as const,
          slug: p.slug,
          title: p.name,
          subtitle: p.subtitle,
          shortDescription: p.shortDescription,
          image: producerCoverMap.get(p.id) || p.coverImageUrl,
          lat: c.lat,
          lng: c.lng,
          href: p.href,
          badges: p.categories.slice(0, 2),
          dateLabel: null,
        };
      });
    }

    // ── Sort for places/farms (non-do modes) ──
    if (mode !== "do" && exploreParams) {
      if (exploreParams.sort === "az") {
        items.sort((a, b) => a.title.localeCompare(b.title, "sv"));
      }
      // "nearest" handled in distance block below
    }

    // ── Distance: compute always if userLocation available, sort only if nearest or nearMe ──
    const effectiveNearMe = nearMeActive && userLocation != null;
    const sortNearest = exploreParams?.sort === "nearest" && userLocation != null;

    if ((effectiveNearMe || sortNearest) && userLocation) {
      items = items.map((item) => ({
        ...item,
        _distanceKm:
          item.lat != null && item.lng != null
            ? distanceKm(userLocation.lat, userLocation.lng, item.lat, item.lng)
            : undefined,
      }));

      if (effectiveNearMe) {
        items = items.filter((item) => item._distanceKm !== undefined && item._distanceKm! <= radiusKm);
      }

      items.sort((a, b) => (a._distanceKm ?? Infinity) - (b._distanceKm ?? Infinity));
    }

    // Favorites filter
    if (favActive) {
      items = items.filter((item) => favIds.has(item.id));
    }

    // Client-side tag filter (places/farms)
    if (exploreParams && exploreParams.tags.length > 0 && mode !== "do") {
      const tagSet = new Set(exploreParams.tags);
      items = items.filter((item) => {
        // Badge-based match (editorial tags)
        if (item.badges.some((b) => tagSet.has(b.toLowerCase()))) return true;
        // Signal-based match: "boende" tag matches actual accommodation presence
        if (tagSet.has("boende") && item.hasAccommodation) return true;
        return false;
      });
    }

    // Client-side search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      items = items.filter(
        (item) =>
          item.title.toLowerCase().includes(q) ||
          item.subtitle.toLowerCase().includes(q) ||
          (item.shortDescription?.toLowerCase().includes(q) ?? false)
      );
    }

    return items;
  }, [
    mode,
    placesResult,
    allProducers,
    allEvents,
    allFleaMarkets,
    nearMeActive,
    userLocation,
    radiusKm,
    favActive,
    favIds,
    searchQuery,
    exploreParams,
    placeCoverMap,
    producerCoverMap,
    eventCoverMap,
    fleaMarketCoverMap,
    placesLoading,
    eventsLoading,
    fleaMarketsLoading,
  ]);

  // Map-compatible items (those with coordinates)
  const mapItems = useMemo(() => results.filter((r) => r.lat != null && r.lng != null), [results]);

  // ── Debug counts ──
  const rawCount =
    mode === "places" ? (placesResult?.items.length ?? 0)
    : mode === "farms" ? (allProducers?.length ?? 0)
    : allEvents.length + allFleaMarkets.length;

  const debugCounts = {
    raw: rawCount,
    normalized: results.length,
    filtered: results.length,
    mappable: mapItems.length,
  };

  if (EXPLORE_DEBUG) {
    const querySource =
      mode === "places"
        ? "places_explore (→ public.places)"
        : mode === "farms"
        ? "producers"
        : "producer_events + flea_markets + producers";

    dbg("counts", `mode=${mode} loading=${isLoading} source=${querySource}`, debugCounts);

    if (!isLoading) {
      if (rawCount > 0) {
        dbg(
          "sample",
          results.slice(0, 3).map((r) => ({
            key: itemKey(r),
            kind: r.kind,
            title: r.title,
            hasCoords: r.lat != null && r.lng != null,
          }))
        );
      }
      if (rawCount === 0) {
        const likelyCause =
          mode === "places"
            ? "places_explore view returns 0 rows — check: 1) RLS/SELECT policy blocks anon/auth, 2) status filter excludes (draft/pending), 3) places table is empty"
            : mode === "farms"
            ? "producers query returns 0 rows"
            : "events query returns 0 rows";
        dbg("breakpoint", `FALL A: rawCount=0 for mode=${mode} — ${likelyCause}`);
      } else if (results.length === 0) dbg("breakpoint", "FALL B/C: raw>0 but results=0 — mapping or filter bug");
      else if (mapItems.length === 0) dbg("breakpoint", "FALL D: results>0 but mappable=0 — no coords");
      else dbg("breakpoint", "OK — mappable>0, markers should render");
    } else {
      dbg("status", "loading — skipping breakpoint analysis");
    }
  }

  // Whether we're showing fallback data instead of the requested mode
  // "do" mode never falls back, so isFallback is only relevant for "places"
  const isFallback = useMemo(() => {
    if (mode !== "places") return false;
    if ((placesResult?.items.length ?? 0) === 0 && results.length > 0) return true;
    return false;
  }, [mode, placesResult, results.length]);

  // ────────────────────────────────────────────────────────────
  // Analytics (SAFE): track only on meaningful view changes
  // ────────────────────────────────────────────────────────────
  const lastViewKeyRef = useRef<string | null>(null);
  const lastEmptyKeyRef = useRef<string | null>(null);
  const lastFallbackRef = useRef<boolean>(false);

  useEffect(() => {
    if (isLoading) return;

    const rawSort = exploreParams?.sort ?? "default";
    const sort = bucketSort(rawSort);

    const hasSearch = !!(searchQuery && searchQuery.trim().length > 0);

    // Keep this LOW cardinality: just yes/no flags + a few modes
    const hasFilters =
      favActive ||
      nearMeActive ||
      (exploreParams
        ? (mode === "do" && exploreParams.when !== "upcoming") ||
          (mode === "do" && exploreParams.contentTypes.length !== 2) || // default = events+outings
          (mode === "places" && exploreParams.tags.length > 0)
        : false);

    const countBucket = bucketCount(results.length);
    const mappableBucket = bucketCount(mapItems.length);
    const radiusBucket = bucketRadiusKm(radiusKm);

    // 1) Results view (deduped)
    const viewKey = JSON.stringify({
      mode,
      countBucket,
      mappableBucket,
      nearMeActive,
      sort,
      hasSearch,
      hasFilters,
      isFallback,
      hasGeo: !!userLocation,
      radiusBucket,
    });

    if (viewKey !== lastViewKeyRef.current) {
      lastViewKeyRef.current = viewKey;

      track(
        "explore_results_view",
        {
          mode,
          count: results.length,
          count_bucket: countBucket,
          mappable_count: mapItems.length,
          mappable_bucket: mappableBucket,
          near_me: nearMeActive,
          near_me_bucket: bucketBool(nearMeActive),
          sort,
          has_search: hasSearch,
          has_search_bucket: bucketBool(hasSearch),
          has_filters: hasFilters,
          has_filters_bucket: bucketBool(hasFilters),
          is_fallback: isFallback,
          is_fallback_bucket: bucketBool(isFallback),
          has_geo: !!userLocation,
          has_geo_bucket: bucketBool(!!userLocation),
          radius_km: radiusKm,
          radius_bucket: radiusBucket,
        } as any,
        "explore"
      );
    }

    // 2) Empty state (deduped per unique conditions)
    if (results.length === 0) {
      const emptyKey = JSON.stringify({
        mode,
        nearMeActive,
        sort,
        hasSearch,
        hasFilters,
        hasGeo: !!userLocation,
        radiusBucket,
      });

      if (emptyKey !== lastEmptyKeyRef.current) {
        lastEmptyKeyRef.current = emptyKey;

        track(
          "explore_empty_state",
          {
            mode,
            near_me: nearMeActive,
            near_me_bucket: bucketBool(nearMeActive),
            sort,
            has_search: hasSearch,
            has_search_bucket: bucketBool(hasSearch),
            has_filters: hasFilters,
            has_filters_bucket: bucketBool(hasFilters),
            has_geo: !!userLocation,
            has_geo_bucket: bucketBool(!!userLocation),
            radius_bucket: radiusBucket,
          } as any,
          "explore"
        );
      }
    }

    // 3) Fallback shown (edge-trigger only when it flips false -> true)
    if (isFallback && !lastFallbackRef.current) {
      lastFallbackRef.current = true;

      track(
        "explore_fallback_shown",
        {
          requested_mode: mode,
          fallback_to: "farms",
          requested_mode_bucket: mode,
        } as any,
        "explore"
      );
    } else if (!isFallback && lastFallbackRef.current) {
      lastFallbackRef.current = false;
    }
  }, [
    isLoading,
    results.length,
    mapItems.length,
    mode,
    nearMeActive,
    userLocation,
    radiusKm,
    favActive,
    exploreParams,
    searchQuery,
    isFallback,
  ]);

  return { results, mapItems, isLoading, isFallback, debugCounts };
}
